import { Controller } from "@hotwired/stimulus"

// Funds section of the owner Finances dashboard — "Fund management in
// dashboard" (Settings → Finances → Dashboard, off by default; Phelipe
// 2026-09-29, SLCC). Opens and closes the funding transfers folded under the
// Funds box, adds / edits / deletes transfers without reloading the page, and
// keeps the box's numbers (approved funds, available funds, the bar) in step
// with the total the server sends back.
export default class extends Controller {
  static targets = [
    "card", "badge", "approved", "source", "committedPct", "available", "bar", "availableHint",
    "toggleButton", "chevron", "toggleLabel", "count", "body", "rows", "empty", "total", "rowTemplate",
    "modal", "modalTitle", "kind", "date", "amount", "journalEntry", "fromAccount", "toAccount", "note",
    "formError", "saveButton", "deleteModal", "deleteMessage"
  ]

  static values = {
    createUrl: String,
    updateUrl: String,
    deleteUrl: String,
    projectId: Number,
    committed: Number,
    currency: String,
    currencyFormat: String,
    kindLabels: Object,
    kindClasses: Object,
    i18n: Object
  }

  connect() {
    this.editingId = null
    this.deletingId = null
    this.deletingRow = null
    this.saving = false
    this._setOpen(this._storedOpen(), false)
  }

  // ── Open / close the transfers ────────────────────────────────────

  toggle() {
    this._setOpen(!this.open, true)
  }

  showTransfers() {
    this._setOpen(true, true)
    this.bodyTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  _setOpen(open, remember) {
    this.open = open
    this.bodyTarget.classList.toggle("hidden", !open)
    this.toggleButtonTarget.setAttribute("aria-expanded", open ? "true" : "false")
    this.chevronTarget.style.transform = open ? "rotate(90deg)" : ""
    this.toggleLabelTarget.textContent = open ? this.i18nValue.hide : this.i18nValue.show_all
    if (remember) {
      try { localStorage.setItem(this._storageKey, open ? "1" : "0") } catch { /* private window */ }
    }
  }

  _storedOpen() {
    try { return localStorage.getItem(this._storageKey) === "1" } catch { return false }
  }

  get _storageKey() {
    return `fundTransfersOpen:${this.projectIdValue}`
  }

  // ── Add / edit ────────────────────────────────────────────────────

  openAdd() {
    this.editingId = null
    this.modalTitleTarget.textContent = this.i18nValue.add_title
    this._fillForm({
      kind: this._hasRows() ? "increase" : "original",
      date_iso: this._todayIso(),
      amount: "", journal_entry: "", from_account: "", to_account: "", note: ""
    })
    this._showModal()
  }

  openEdit(event) {
    const data = this._rowData(event.currentTarget.closest("[data-transfer-row]"))
    if (!data) return
    this.editingId = data.id
    this.modalTitleTarget.textContent = this.i18nValue.edit_title
    this._fillForm({ ...data, amount: this._formatNumber(data.amount) })
    this._showModal()
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    this.editingId = null
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async save() {
    if (this.saving) return
    const amount = this._parseNumber(this.amountTarget.value)
    if (!(amount > 0)) return this._setError(this.i18nValue.need_amount)
    const date = (this.dateTarget.value || "").trim()
    if (!date) return this._setError(this.i18nValue.need_date)

    const editing = this.editingId !== null
    const body = {
      funding_transfer: {
        kind: this.kindTarget.value,
        transfer_date: date,
        amount: amount.toFixed(2),
        journal_entry: this.journalEntryTarget.value,
        from_account: this.fromAccountTarget.value,
        to_account: this.toAccountTarget.value,
        note: this.noteTarget.value
      }
    }
    if (editing) body.transfer_id = this.editingId

    this.saving = true
    this.saveButtonTarget.disabled = true
    try {
      const resp = await fetch(editing ? this.updateUrlValue : this.createUrlValue, {
        method: editing ? "PATCH" : "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        this._setError(data.error || this.i18nValue.error)
        return
      }
      this._upsertRow(data.transfer)
      this._applyTotals(data)
      this.closeModal()
      this._setOpen(true, true)
      const template = editing ? this.i18nValue.updated : this.i18nValue.added
      this._showToast(template.replace("%{amount}", this._money(data.funds_total)))
    } catch {
      this._setError(this.i18nValue.error)
    } finally {
      this.saving = false
      this.saveButtonTarget.disabled = false
    }
  }

  // ── Delete ────────────────────────────────────────────────────────

  askDelete(event) {
    const row = event.currentTarget.closest("[data-transfer-row]")
    const data = this._rowData(row)
    if (!data) return
    this.deletingRow = row
    this.deletingId = data.id
    const template = data.kind === "decrease" ? this.i18nValue.delete_up : this.i18nValue.delete_down
    this.deleteMessageTarget.textContent = template.replace("%{amount}", this._money(Math.abs(data.amount)))
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() {
    this.deleteModalTarget.classList.add("hidden")
    this.deletingRow = null
    this.deletingId = null
  }

  async confirmDelete() {
    if (this.deletingId === null) return
    const row = this.deletingRow
    const url = `${this.deleteUrlValue}?transfer_id=${encodeURIComponent(this.deletingId)}`
    try {
      const resp = await fetch(url, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        this._showToast(data.error || this.i18nValue.error, "error")
        return
      }
      if (row) row.remove()
      this._applyTotals(data)
      this._showToast(this.i18nValue.deleted.replace("%{amount}", this._money(data.funds_total)))
    } catch {
      this._showToast(this.i18nValue.error, "error")
    } finally {
      this.closeDelete()
    }
  }

  // ── Rows ──────────────────────────────────────────────────────────

  _upsertRow(transfer) {
    if (!transfer) return
    let row = this.rowsTarget.querySelector(`[data-transfer-id="${transfer.id}"]`)
    if (!row) row = this.rowTemplateTarget.content.querySelector("[data-transfer-row]").cloneNode(true)
    this._fillRow(row, transfer)

    // Keep them in date order, oldest first.
    const key = row.dataset.sortKey
    const next = Array.from(this.rowsTarget.querySelectorAll("[data-transfer-row]"))
      .find((other) => other !== row && other.dataset.sortKey > key)
    this.rowsTarget.insertBefore(row, next || null)
  }

  _fillRow(row, t) {
    const decrease = t.kind === "decrease"
    row.dataset.transferId = t.id
    row.dataset.sortKey = `${t.date_iso}-${String(t.id).padStart(10, "0")}`
    row.dataset.transfer = JSON.stringify(t)
    this._field(row, "date").textContent = t.date || ""
    const kind = this._field(row, "kind")
    kind.textContent = this.kindLabelsValue[t.kind] || t.kind
    kind.className = `inline-flex items-center h-6 px-2 rounded-md text-[13px] font-medium ${this.kindClassesValue[t.kind] || ""}`
    this._field(row, "from_account").textContent = t.from_account || ""
    this._field(row, "to_account").textContent = t.to_account || ""
    this._field(row, "journal_entry").textContent = t.journal_entry || ""
    this._field(row, "note").textContent = t.note || ""
    const addedBy = this._field(row, "added_by")
    addedBy.textContent = t.added_by || ""
    addedBy.title = t.added_on ? (this.i18nValue.added_on || "%{date}").replace("%{date}", t.added_on) : ""
    const amount = this._field(row, "amount")
    amount.textContent = `${decrease ? "-" : "+"}${this.currencyValue}${this._formatNumber(Math.abs(t.amount))}`
    amount.classList.toggle("text-orange-700", decrease)
    amount.classList.toggle("text-gray-700", !decrease)
  }

  _field(row, name) {
    return row.querySelector(`[data-field="${name}"]`)
  }

  _rowData(row) {
    if (!row) return null
    try { return JSON.parse(row.dataset.transfer || "null") } catch { return null }
  }

  _hasRows() {
    return !!this.rowsTarget.querySelector("[data-transfer-row]")
  }

  // ── The Funds box numbers ─────────────────────────────────────────

  _applyTotals(data) {
    const i18n = this.i18nValue
    const funds = Number(data.funds_total) || 0
    const count = Number(data.count) || 0
    const committed = this.committedValue
    const available = funds - committed
    const hasFunds = funds > 0
    const over = hasFunds && available < 0
    const usedPct = hasFunds ? Math.min(100, (committed / funds) * 100) : 0
    const pctText = (Math.round(usedPct * 10) / 10).toFixed(1)

    this.approvedTarget.textContent = this._money(funds)
    this.availableTarget.textContent = this._money(available)
    this.availableTarget.classList.remove("text-gray-500", "text-red-600", "text-green-700")
    this.availableTarget.classList.add(!hasFunds ? "text-gray-500" : (over ? "text-red-600" : "text-green-700"))
    this.barTarget.style.width = `${usedPct}%`
    this.barTarget.classList.toggle("bg-red-500", over)
    this.barTarget.classList.toggle("bg-gray-600", !over)
    this.committedPctTarget.textContent = hasFunds ? i18n.committed_pct.replace("%{pct}", pctText) : i18n.no_funds
    if (!hasFunds) {
      this.availableHintTarget.textContent = i18n.available_none
    } else if (over) {
      this.availableHintTarget.textContent = i18n.available_over.replace("%{amount}", this._money(Math.abs(available)))
    } else {
      this.availableHintTarget.textContent = i18n.available_hint.replace("%{pct}", pctText)
    }

    if (count > 0) {
      this.sourceTarget.textContent = count === 1 ? i18n.source_one : i18n.source_other.replace("%{count}", count)
    } else {
      this.sourceTarget.textContent = i18n.source_none
    }
    this.countTarget.textContent = count === 1 ? i18n.count_one : i18n.count_other.replace("%{count}", count)
    this.totalTarget.textContent = this._money(funds)
    this.emptyTarget.classList.toggle("hidden", count > 0)

    this.cardTarget.classList.toggle("border-red-200", over)
    this.cardTarget.classList.toggle("border-gray-200", !over)
    const red = ["bg-red-50", "text-red-600", "border-red-200"]
    const green = ["bg-green-50", "text-green-700", "border-green-200"]
    this.badgeTarget.classList.remove(...red, ...green)
    this.badgeTarget.classList.add(...(over ? red : green))
  }

  // ── Form helpers ──────────────────────────────────────────────────

  _fillForm(d) {
    this.kindTarget.value = d.kind || "increase"
    this._setDate(d.date_iso)
    this.amountTarget.value = d.amount || ""
    this.journalEntryTarget.value = d.journal_entry || ""
    this.fromAccountTarget.value = d.from_account || ""
    this.toAccountTarget.value = d.to_account || ""
    this.noteTarget.value = d.note || ""
    this._setError("")
  }

  _setDate(iso) {
    const picker = this.dateTarget._flatpickr
    if (picker) {
      if (iso) picker.setDate(iso, false)
      else picker.clear()
    } else {
      this.dateTarget.value = iso || ""
    }
  }

  _showModal() {
    this.modalTarget.classList.remove("hidden")
    setTimeout(() => this.amountTarget.focus(), 30)
  }

  _setError(message) {
    this.formErrorTarget.textContent = message || ""
    this.formErrorTarget.classList.toggle("hidden", !message)
  }

  _todayIso() {
    const d = new Date()
    const pad = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  }

  // ── Numbers (same rules as the dashboard's own controller) ────────

  _parseNumber(str) {
    if (!str) return 0
    let cleaned = String(str).trim().replace(/^[^\d-]+/, "")
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned)
    return isNaN(val) ? 0 : val
  }

  _formatNumber(num) {
    const abs = Math.abs(Number(num) || 0)
    if (this.currencyFormatValue === "european_currency_format") {
      return abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    }
    return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  _money(value) {
    const num = Number(value) || 0
    return `${num < 0 ? "-" : ""}${this.currencyValue}${this._formatNumber(num)}`
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
